/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Favorite Thunk
 */

import { selectIsOnline } from '@zcode-ui/web-zumper-global-state'
import { AnalyticsC, localStorageSingleton, trackGAEvent } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import { warnOffline } from '@zcode/nr/es/shared/modules/app/thunks'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { PROPERTY_FAVORITED } from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import Marketing from '@zcode/nr/es/shared/utils/marketing'
import { logger } from '../logger'
import { addListItem } from '../modules/favoritesView/actions/index'
import { addErrorButterBar } from './butter.action'
import { addFavorites, removeFavorites } from './index'

export const toggleEntityFavorite = (args) => (dispatch, getState) => {
  const state = getState()
  const isOnline = selectIsOnline(state)
  const { entity, isFavorite, isLoggedIn, analyticsOrigin, user } = args
  if (!isOnline && isLoggedIn) {
    dispatch(warnOffline())
    return
  }
  const { buildingId, isBuilding, listingId, uId } = entity
  const timestamp = ~~(Date.now() / 1000)
  const entityIds = {}
  if (isBuilding) {
    entityIds.buildingId = Number(buildingId)
  } else {
    entityIds.listingId = Number(listingId)
  }

  const analyticsOriginText = `${analyticsOrigin}-logged ${isLoggedIn ? 'in' : 'out'}`
  if (isFavorite) {
    trackGAEvent(AnalyticsC.FAVORITE, 'Unfavorited', analyticsOriginText)
    // Optimistic Un-favoriting
    dispatch(removeFavorites(entityIds, timestamp))
    // Let server know to remove favorite
    if (isLoggedIn) {
      Api.favorites.remove(entityIds).catch(() => {
        // Failed, add favorite back
        dispatch(addFavorites(entityIds, timestamp))
        dispatch(
          addErrorButterBar({
            message: 'Failed to remove from favorites, try again',
            isFavoriteButter: true,
          })
        )
      })
    } else {
      // Remove from LocalStorage
      try {
        const favorites = localStorageSingleton.get('favorites')
        if (favorites) {
          delete favorites.buildings[entityIds.buildingId]
          delete favorites.listings[entityIds.listingId]
          localStorageSingleton.set('favorites', favorites)
        }
      } catch (e) {
        logger.warn('unfavorite:', uId, e)
      }
    }
  } else {
    dispatch(Optimizely.track({ eventKey: PROPERTY_FAVORITED }))
    trackGAEvent(AnalyticsC.FAVORITE, 'Favorited', analyticsOriginText)
    if (entity.isTrackableBlueshiftRecombee) {
      Marketing.trackFavorite(buildingId, listingId, user?.email)
    }
    // Optimistic favoriting
    dispatch(addFavorites(entityIds, timestamp))
    if (isLoggedIn) {
      Api.favorites
        .add(entityIds)
        .then(() => {
          dispatch(addListItem({ entity }))
        })
        .catch(() => {
          dispatch(
            addErrorButterBar({
              message: 'Failed to favorite, try again',
              isFavoriteButter: true,
            })
          )
        })
    } else {
      // Add to LocalStorage
      const favorites = localStorageSingleton.get('favorites') || { listings: {}, buildings: {} }
      const { buildingId, listingId } = entity
      if (buildingId) {
        favorites.buildings[buildingId] = timestamp
      } else if (listingId) {
        favorites.listings[listingId] = timestamp
      }
      localStorageSingleton.set('favorites', favorites)
    }
  }
}
